import { useCallback } from "react";
import { useI18nContext } from "@hopper-b2b/i18n";

import type { SeatSelectionErrorModal } from "../../types/common";
import {
  getSeatSelectionErrorConfig,
  SEAT_SELECTION_ERROR_CONFIG,
} from "./config";
import { FlightErrorAction, type FlightErrorConfig } from "./types";

/**
 * Props returned by the hook for rendering seat selection error modals
 */
export interface SeatSelectionErrorModalProps {
  title: string;
  subtitle: string;
  primaryButtonText: string;
  primaryOnClick: () => void;
  secondaryButtonText?: string;
  secondaryOnClick?: () => void;
  className?: string;
  /** Raw action type for tracking/analytics */
  primaryAction: FlightErrorAction;
  secondaryAction?: FlightErrorAction;
  /** Modal type for tracking */
  modalType: string;
}

/**
 * Action handlers for seat selection errors
 */
export interface SeatSelectionErrorActionHandlers {
  /** Navigate to flight search page */
  navigateToFlightSearch: () => void;
  /** Continue checkout without seat selection */
  continueWithoutSeats: () => void;
  /** Clear error and close modal */
  clearError: () => void;
}

/**
 * Tenant-specific overrides for seat selection error configurations.
 * Use this to customize error messages, buttons, or actions for specific tenants.
 */
export interface SeatSelectionErrorConfigOverrides {
  /** Override specific SeatSelectionErrorModal configurations */
  seatErrors?: Partial<Record<SeatSelectionErrorModal, FlightErrorConfig>>;
  /** Override the default error configuration */
  default?: FlightErrorConfig;
}

/**
 * Hook options
 */
export interface UseSeatSelectionErrorConfigOptions {
  /** Action handlers for button clicks */
  handlers: SeatSelectionErrorActionHandlers;
  /**
   * Tenant-specific overrides for error configurations.
   * These will be merged with the base config, allowing tenants to customize
   * specific error messages, buttons, or actions.
   *
   * @example
   * ```tsx
   * configOverrides: {
   *   seatErrors: {
   *     [SeatSelectionErrorModal.FailedAddToCart]: {
   *       titleKey: "perkspot.seatAddFailedTitle",
   *       subtitleKey: "perkspot.seatAddFailedSubtitle",
   *       primaryButton: { textKey: "perkspot.continueWithoutSeats", action: FlightErrorAction.ContinueWithoutSeats },
   *       secondaryButton: { textKey: "perkspot.tryAgain", action: FlightErrorAction.ClearError },
   *     },
   *   },
   * }
   * ```
   */
  configOverrides?: SeatSelectionErrorConfigOverrides;
}

/**
 * Maps an action type to its handler function
 */
const mapActionToHandler = (
  action: FlightErrorAction,
  handlers: SeatSelectionErrorActionHandlers
): (() => void) => {
  switch (action) {
    case FlightErrorAction.NavigateToFlightSearch:
      return handlers.navigateToFlightSearch;
    case FlightErrorAction.ContinueWithoutSeats:
      return handlers.continueWithoutSeats;
    case FlightErrorAction.ClearError:
    case FlightErrorAction.None:
    default:
      return handlers.clearError;
  }
};

/**
 * Hook to get seat selection error configuration and map it to modal props
 *
 * @example
 * ```tsx
 * const { getSeatSelectionErrorProps, getDefaultProps } = useSeatSelectionErrorConfig({
 *   handlers: {
 *     navigateToFlightSearch: () => navigate("/flights/search"),
 *     continueWithoutSeats: () => { clearError(); goNext(); },
 *     clearError: () => send(SeatEventTypes.CLEAR_SEAT_ERROR),
 *   },
 * });
 *
 * const props = getSeatSelectionErrorProps(SeatSelectionErrorModal.FailedAddToCart);
 * ```
 *
 * @example With tenant overrides
 * ```tsx
 * const { getSeatSelectionErrorProps } = useSeatSelectionErrorConfig({
 *   handlers: { ... },
 *   configOverrides: {
 *     seatErrors: {
 *       [SeatSelectionErrorModal.FailedAddToCart]: {
 *         titleKey: "perkspot.seatAddFailedTitle",
 *         subtitleKey: "perkspot.seatAddFailedSubtitle",
 *         primaryButton: { textKey: "perkspot.continueWithoutSeats", action: FlightErrorAction.ContinueWithoutSeats },
 *       },
 *     },
 *   },
 * });
 * ```
 */
export const useSeatSelectionErrorConfig = ({
  handlers,
  configOverrides,
}: UseSeatSelectionErrorConfigOptions) => {
  const { t } = useI18nContext();

  /**
   * Get merged seat selection error config with overrides
   */
  const getMergedSeatErrorConfig = useCallback(
    (errorCode: SeatSelectionErrorModal): FlightErrorConfig => {
      // Check for override first
      if (configOverrides?.seatErrors?.[errorCode]) {
        return configOverrides.seatErrors[errorCode];
      }
      // Fall back to base config
      return getSeatSelectionErrorConfig(errorCode);
    },
    [configOverrides]
  );

  /**
   * Get the default config (with potential override)
   */
  const getDefaultConfig = useCallback((): FlightErrorConfig => {
    if (configOverrides?.default) {
      return configOverrides.default;
    }
    return SEAT_SELECTION_ERROR_CONFIG.default;
  }, [configOverrides]);

  /**
   * Converts a FlightErrorConfig to SeatSelectionErrorModalProps
   */
  const configToProps = useCallback(
    (
      config: FlightErrorConfig,
      modalType: string
    ): SeatSelectionErrorModalProps => {
      const primaryAction = config.primaryButton.action;
      const secondaryAction = config.secondaryButton?.action;

      return {
        title: t(config.titleKey),
        subtitle: t(config.subtitleKey),
        primaryButtonText: t(config.primaryButton.textKey),
        primaryOnClick: mapActionToHandler(primaryAction, handlers),
        primaryAction,
        ...(config.secondaryButton && {
          secondaryButtonText: t(config.secondaryButton.textKey),
          secondaryOnClick: mapActionToHandler(
            config.secondaryButton.action,
            handlers
          ),
          secondaryAction,
        }),
        className: config.className,
        modalType,
      };
    },
    [t, handlers]
  );

  /**
   * Get modal props for a specific SeatSelectionErrorModal code
   */
  const getSeatSelectionErrorProps = useCallback(
    (errorCode: SeatSelectionErrorModal): SeatSelectionErrorModalProps => {
      const config = getMergedSeatErrorConfig(errorCode);
      return configToProps(config, `SeatSelection.${errorCode}`);
    },
    [getMergedSeatErrorConfig, configToProps]
  );

  /**
   * Get the default error modal props
   */
  const getDefaultProps = useCallback((): SeatSelectionErrorModalProps => {
    const config = getDefaultConfig();
    return configToProps(config, "default");
  }, [getDefaultConfig, configToProps]);

  return {
    /** Get modal props for a specific SeatSelectionErrorModal code */
    getSeatSelectionErrorProps,
    /** Get the default error modal props */
    getDefaultProps,
  };
};
